import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface ResponsiveSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  closeLabel: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

/**
 * Phone (<768): bottom sheet (slides from bottom).
 * Tablet (>=768): right side sheet.
 */
export function ResponsiveSheet({ open, onClose, title, closeLabel, children, footer }: ResponsiveSheetProps) {
  const isMobile = useIsMobile();

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const initial = isMobile ? { y: "100%" } : { x: "100%" };
  const animateTo = isMobile ? { y: 0 } : { x: 0 };
  const exit = isMobile ? { y: "100%" } : { x: "100%" };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.button
            type="button"
            aria-label={closeLabel}
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-40 bg-biz-foreground/40 backdrop-blur-[2px] biz-motion-safe"
          />
          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={initial}
            animate={animateTo}
            exit={exit}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className={cn(
              "fixed z-50 bg-biz-surface-elevated text-biz-foreground biz-shadow-lg flex flex-col biz-motion-safe",
              isMobile
                ? "left-0 right-0 bottom-0 max-h-[90vh] rounded-t-biz-sheet border-t border-biz-border"
                : "right-0 top-0 h-full w-[min(480px,100vw)] rounded-l-biz-sheet border-l border-biz-border",
            )}
          >
            {isMobile && (
              <div className="flex justify-center pt-2" aria-hidden>
                <div className="h-1 w-10 rounded-full bg-biz-border-strong" />
              </div>
            )}
            <header className="flex items-center justify-between px-5 py-3 border-b border-biz-border">
              <h2 className="font-display text-lg font-semibold truncate">{title}</h2>
              <button
                type="button"
                onClick={onClose}
                aria-label={closeLabel}
                className="inline-flex items-center justify-center h-11 w-11 rounded-biz-btn text-biz-foreground-muted hover:bg-biz-muted hover:text-biz-foreground transition-colors biz-focus-ring biz-motion-safe"
              >
                <X size={20} weight="regular" />
              </button>
            </header>
            <div className="flex-1 overflow-y-auto px-5 py-4 biz-scrollbar">{children}</div>
            {footer && (
              <footer className="px-5 py-4 border-t border-biz-border bg-biz-surface">{footer}</footer>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
